import type { AssetClassConstants } from '@/constants/AssetClassConstants';
import type { Currency } from '@/constants/Currency';
import type { FactorConstants } from '@/constants/FactorConstants';
import type { Region } from '@/constants/Region';
import type { ReturnCategoryConstants } from '@/constants/ReturnCategoryConstants';
import type { ReturnTypeConstants } from '@/constants/ReturnTypeConstants';
import type { StyleConstants } from '@/constants/StyleConstants';
import type { TimeSeriesEntityTypeConstants } from '@/constants/TimeSeriesEntityTypeConstants';
import type { TrackTypeConstants } from '@/constants/TrackTypeConstants';
import type { PossiblyLiveObject } from '@/types/LiveObjectAccessRightDTO';
import type { TimeSeriesEntityStatus } from '@/types/admin/strategies/StrategyStatus';

export type DSBStrategyItemResponseDTOBase = StrategyItemResponseDTO & {
  virtualKey: string;
  displayName: string;
  favoriteId?: string;
};

export type DSBStrategyItemResponseDTO =
  | DSBStrategyItemResponseDTOBase
  | (DSBStrategyItemResponseDTOBase & PossiblyLiveObject);

export interface StrategyItemResponseDTO {
  id: string;
  trackType: TrackTypeConstants;
  type: TimeSeriesEntityTypeConstants;
  code: string;
  shortName: string;
  longName: string;
  assetClass: AssetClassConstants;
  currency: Currency;
  region: Region;
  factor: FactorConstants;
  style: StyleConstants;
  returnType: ReturnTypeConstants | null;
  returnCategory: ReturnCategoryConstants | null;
  status?: TimeSeriesEntityStatus | null;
  retirementDate?: string | null;
  indexSource: string;
  inceptionDate: string | null;
  historyStartDate: string | null;
  description: string | null;
  isFuturized: boolean | null;
  /** Classification category id; resolve to display name via discover strategy categories. */
  classificationId?: number | null;

  /**
   * It's the strategy code we manually set in the useDiscoverStore.ts and useDataDiscovery.ts
   */
  virtualKey?: string;

  /** @deprecated */
  provider: string | null;
  providerId: string | null;
  isSuspended: boolean;
  ticker?: string[];

  /* Strategies and Thematics */
  volTarget?: string | null;

  /* Morningstar (Fund and Benchmark) */
  isin?: string[];
  morningstarId?: string[];

  /* This field is only used for eVestment (Fund and ETF) */
  universeName?: string | null;
  investmentFocus?: string | null;

  /* Stocks */
  sedol?: string[];
  ric?: string[];
  esgRating?: string | null;
  sector?: string | null;
  country?: string | null;

  /* Thematics */
  theme?: string | null;
  esg?: string | null;
  isFlagship?: boolean | null;

  /* Private Tracks and Proxy */
  internalReference?: string | null;

  /* Proxy */
  isProxy?: true;
  proxy?: StrategyItemResponseDTO;
  proxyCode?: string;
}

export interface TrackLibraryStrategy extends StrategyItemResponseDTO {
  /**
   * Property added on the front end, based on the user's display name preference
   */
  displayName: string;
}

export const isStrategyItemResponseDTO = (data: unknown): data is StrategyItemResponseDTO => {
  return typeof data === 'object' && data !== null && 'id' in data && 'code' in data;
};
